/**
 * PostHog Event Registry — single source of truth for all custom events.
 *
 * Why a registry?
 *  - Type-safe `trackEvent` and `trackServerEvent` calls (compile-time check on
 *    event name + property shape).
 *  - PHI guard: every emitted event is filtered through `sanitizeProperties`,
 *    which strips a denylist of known PHI keys and warns in development.
 *  - Inventory: this file is the canonical list of what we capture; pair it
 *    with `docs/analytics/POSTHOG-EVENT-TAXONOMY.md`.
 *
 * Naming convention: `{domain}_{object}_{action}` (snake_case, past-tense
 * verb where applicable). Examples: `sam_case_created`, `ticket_status_changed`.
 *
 * Property discipline (HIPAA):
 *  - Allowed: opaque IDs (sax_id, practice_id, tenant_id, case_id, ticket_id,
 *    patient_id), enums, counts, durations (ms), booleans, buckets.
 *  - Forbidden: names, DOB, MRN, addresses, emails (outside `identify`),
 *    phone numbers, diagnoses, free-text, search terms (use length only),
 *    document filenames, message bodies.
 */

// ---------------------------------------------------------------------------
// Property primitives
// ---------------------------------------------------------------------------

export type EventOutcome = 'success' | 'error' | 'cancelled';

export type DurationBucket =
  | 'lt_100ms'
  | 'lt_500ms'
  | 'lt_1s'
  | 'lt_5s'
  | 'lt_30s'
  | 'gt_30s';

export type CountBucket =
  | '0'
  | '1'
  | '2_5'
  | '6_25'
  | '26_100'
  | '101_1000'
  | 'gt_1000';

// ---------------------------------------------------------------------------
// Event registry — every PostHog event the app may emit.
// ---------------------------------------------------------------------------

export interface AppEventMap {
  // ----- Phase 2 — Login & activation funnel ------------------------------
  login_initiated: {
    source: 'login_page' | 'middleware_redirect' | 'session_expired';
  };
  login_succeeded: {
    sax_id: string | number;
    practice_id?: string;
    tenant_id?: string;
    login_method?: string;
    is_internal_user?: boolean;
  };
  login_failed: {
    error_code?: string;
    stage: 'auth0_callback' | 'session_save' | 'token_refresh';
  };
  mfa_challenge_shown: { factor?: string };
  session_expired_redirect: { from_path?: string };
  first_action_after_login: {
    action: string;
    seconds_since_login?: number;
  };

  // ----- Phase 3 — Patient chart & conversion funnel ----------------------
  patient_chart_opened: { source?: string; patient_id?: string };
  patient_chart_tab_viewed: { tab: string; previous_tab?: string };
  patient_section_saved: {
    section: string;
    success: boolean;
    duration_ms?: number;
  };
  patient_document_action: {
    action: 'view' | 'download' | 'delete' | 'upload';
    document_type_code?: string;
    success?: boolean;
  };
  patient_task_action: {
    action: 'create' | 'assign' | 'status_change';
    success?: boolean;
  };
  patient_transfer_stage: {
    stage: 'initiated' | 'confirmed' | 'cancelled' | 'failed';
    error_message?: string;
  };
  patient_optout_toggled: { new_state: boolean };
  patient_followup_flag_set: { flag_state: boolean };
  /**
   * Fired server-side when a clinical note is created or updated.
   * Visibility is structural (practice / private), not PHI.
   */
  clinical_note_saved: {
    action: 'create' | 'update' | 'reply';
    visibility?: string;
    success: boolean;
  };
  // Conversion funnel stages — emit at API success callback
  vob_completed: { practice_id?: string; outcome?: string };
  hst_ordered: { practice_id?: string };
  hst_resulted: { practice_id?: string; outcome?: string };
  consult_scheduled: { practice_id?: string };
  prezo_completed: { practice_id?: string };
  treatment_started: { practice_id?: string };

  // ----- Phase 4 — Task Center & IS Board ---------------------------------
  ticket_created: { type?: string; required_assignment?: boolean };
  ticket_status_changed: {
    from_status?: string;
    to_status?: string;
    ticket_age_hours?: number;
  };
  ticket_assigned: {
    has_assignee: boolean;
    self_assigned?: boolean;
  };
  ticket_reply_sent: {
    thread: 'ticket' | 'task';
    reply_count?: number;
  };
  ticket_archived: { ticket_age_hours?: number };
  task_kanban_card_moved: {
    from_column?: string;
    to_column?: string;
  };
  task_filter_changed: { filter_name: string };
  task_search: { query_length: number };
  is_ticket_created: { ticket_type_bucket?: string };
  is_ticket_status_changed: {
    from_status?: string;
    to_status?: string;
  };
  is_ticket_assigned: { has_assignee: boolean };
  is_kanban_card_moved: {
    from_column?: string;
    to_column?: string;
  };

  // ----- Phase 5 — SAM (Sleep Appliance Management) -----------------------
  sam_case_list_viewed: { filter_count?: number };
  sam_case_created: {
    service_type?: string;
    has_insurance?: boolean;
    success: boolean;
  };
  sam_case_status_transition: {
    from_status?: string;
    to_status?: string;
    days_in_prev_status?: number;
  };
  sam_case_updated: { fields_changed?: number; success: boolean };
  sam_invoice_generated: { case_id?: string; success: boolean };
  sam_invoice_emailed: { success: boolean };
  sam_payment_recorded: { method_bucket?: string; success: boolean };
  sam_shipment_created: { carrier?: string; success: boolean };
  sam_appliance_delivered: { case_id?: string };
  sam_admin_entity_saved: {
    entity: 'vendor' | 'service_type' | 'appliance' | 'practice_appliance';
    action: 'create' | 'update' | 'delete';
    success: boolean;
  };

  // ----- Phase 6 — Alora AI, DreamChat, Labs ------------------------------
  alora_session_started: { source?: string };
  alora_message_sent: {
    message_length_bucket: CountBucket;
    has_tool_call?: boolean;
  };
  alora_tool_invoked: { tool_name: string };
  alora_response_received: {
    latency_ms_bucket: DurationBucket;
    outcome: EventOutcome;
    token_count_bucket?: CountBucket;
  };
  alora_export_clicked: { format: string };
  chat_session_started: { channel_type?: string };
  chat_message_sent: {
    channel_type?: string;
    has_attachment?: boolean;
  };
  chat_channel_switched: { channel_type?: string };
  chat_search_used: { query_length: number };
  chat_connection_state: {
    connection_state: 'connected' | 'reconnecting' | 'offline' | 'error';
  };
  lab_screen_viewed: { lab: string };
  lab_ai_run_completed: { lab: string; outcome: EventOutcome };

  // ----- Phase 7 — Reports & Admin consoles -------------------------------
  report_viewed: { report: string };
  report_filter_applied: { report: string; filter_name: string };
  report_date_range_changed: { report: string; preset?: string };
  report_export_started: {
    report: string;
    format: string;
    row_count_bucket?: CountBucket;
  };
  report_export_completed: {
    report: string;
    format: string;
    success: boolean;
  };
  report_drilldown: { report: string; target: string };
  admin_screen_viewed: { screen: string };
  admin_search: { screen: string; query_length: number };
  admin_filter_applied: { screen: string; filter_key: string };
  admin_export_clicked: { screen: string; format?: string };
  admin_record_action: {
    screen: string;
    action: 'open' | 'save' | 'cancel' | 'delete';
    success?: boolean;
  };

  // ----- HST Order Form — granular funnel & error events ------------------
  hst_form_loaded: {
    form_assignment_id: string;
    has_amount: boolean;
    stripe_test_mode: boolean;
  };
  hst_stripe_loaded: { form_assignment_id: string; load_ms: number };
  hst_stripe_load_timeout: {
    form_assignment_id: string;
    timeout_ms: number;
  };
  hst_stripe_fallback_shown: { form_assignment_id: string };
  hst_payment_method_created: {
    form_assignment_id: string;
    duration_ms: number;
    stripe_test_mode: boolean;
  };
  hst_payment_method_failed: {
    form_assignment_id: string;
    stripe_error_code?: string;
    stripe_test_mode: boolean;
  };
  hst_form_submitted: {
    form_assignment_id: string;
    success: boolean;
    attempt_number: number;
    stripe_test_mode: boolean;
  };
  /** Cash HST ordered from the /history Results table. */
  cash_hst_order_created: { success: boolean; chart_created: boolean };

  // ----- Phase 8 — Forms & Dashboard --------------------------------------
  form_started: { form: string; source?: string };
  form_step_completed: { form: string; step_index: number };
  form_abandoned: {
    form: string;
    last_step?: number;
    time_spent_ms?: number;
  };
  form_submitted: { form: string; success: boolean };
  dashboard_metric_drilldown: { metric: string };
  dashboard_date_range_changed: { preset?: string };
  dashboard_assisted_form_link_generated: { success: boolean };

  // ----- Phase 9 — Notifications & errors ---------------------------------
  notification_dropdown_opened: { unread_count_bucket?: CountBucket };
  notification_clicked: { notification_type?: string };
  notification_dismissed: { notification_type?: string };
  notification_mark_all_read: { count_bucket?: CountBucket };
  client_error_boundary_triggered: {
    error_name?: string;
    is_chunk_error?: boolean;
    route?: string;
  };

  // ----- Phase 11 — Performance / Core Web Vitals -------------------------
  // Captured via next/web-vitals in providers/PostHogProvider.tsx. All
  // values are numeric measurements only — no DOM context, no PHI.
  web_vital_captured: {
    metric: 'LCP' | 'CLS' | 'INP' | 'FCP' | 'TTFB' | 'FID';
    value: number;
    rating?: 'good' | 'needs-improvement' | 'poor';
    route?: string;
    navigation_type?: string;
  };

  // ----- Phase 12 — Booking page traffic & link sharing -------------------
  // Fires on every load of /book/[provider], /book/type/[appointmentTypeId],
  // and /results. Anonymous traffic (no logged-in user) is the norm here. PostHog's
  // built-in `$pageview` is also captured by PostHogProvider; this typed
  // event adds structured props (surface, share-tag attribution) that the
  // generic pageview does not carry.
  booking_page_viewed: {
    surface: 'provider' | 'appointment_type' | 'results';
    appointment_type_id?: string;
    tenant_id?: string;
    practice_id?: string;
    has_share_tag: boolean;
    share_source?: string;
    shared_by_sax_id?: string;
    is_authenticated: boolean;
  };
  // Fires when a staff member copies a booking link from any of the
  // appointment-type / availability surfaces. The destination URL is
  // tagged with `?src=copy&u=<saxId>` so a later `booking_page_viewed`
  // can be attributed back to the staff member who shared it.
  booking_link_shared: {
    surface:
      | 'availability_page_table'
      | 'practice_picker_dialog'
      | 'create_type_dialog'
      | 'edit_type_dialog'
      | 'assign_staff_dialog'
      | 'scheduling_defaults_card';
    scope: 'global' | 'tenant' | 'practice';
    appointment_type_id?: string;
    practice_id?: string;
    tenant_id?: string;
  };

  // ----- Booking funnel — submit / reschedule / cancel --------------------
  // Fires on the patient's booking form submit (all three booking surfaces:
  // /book/[provider], /book/type/[appointmentTypeId], /results).
  // No PHI — appointment_type_id and tenant/practice IDs are opaque.
  booking_submitted: {
    surface: 'provider' | 'appointment_type' | 'results';
    outcome: EventOutcome;
    appointment_type_id?: string;
    tenant_id?: string;
    practice_id?: string;
    error_code?: string;
  };
  // Fires when a patient completes the reschedule flow.
  booking_reschedule_submitted: {
    outcome: EventOutcome;
    appointment_type_id?: string;
    tenant_id?: string;
    practice_id?: string;
    error_code?: string;
  };
  // Fires when a patient confirms appointment cancellation.
  booking_cancelled: {
    outcome: EventOutcome;
    appointment_type_id?: string;
    tenant_id?: string;
    practice_id?: string;
    error_code?: string;
  };
  // Fired server-side when a patient opens a stable meeting redirect URL.
  meeting_link_opened: {
    appointment_type_short_code: string;
  };

  // ----- Booking timeline observability (Activity Detail) -----------------
  // Defense-in-depth telemetry on the cross-tenant Activity Detail bug
  // (see docs/bookings/HANDOFF-LOGS-TENANT-DERIVATION-RESOLVED.md and § 10 Finding 10
  // in docs/bookings/AI-AGENT-GUIDE.md). The Next.js logs route resolves the
  // booking's real tenant_id via lambda-booking-get before invoking
  // lambda-booking-send-logs. These two events make the silent failure
  // modes loud in PostHog so the bug cannot recur unnoticed.
  //
  // Fires when `resolveAppointmentTenantAndUuid` in
  // `app/api/booking/appointments/[id]/logs/route.ts` cannot resolve the
  // booking's tenant via booking-get and the route falls back to the
  // session tenant. A non-zero rate of this event in production indicates
  // either `BOOKING_GET_LAMBDA` is unset somewhere or booking-get is
  // failing/returning malformed responses. Either way the user sees a
  // possibly-empty Activity panel for cross-tenant bookings.
  booking_logs_tenant_fallback: {
    reason:
      | 'lambda_unset'
      | 'invoke_failed'
      | 'invalid_response'
      | 'invalid_shape';
    identifier_kind: 'uuid' | 'booking_code';
    // Opaque UUID of the session tenant (never the booking's tenant — by
    // definition we don't know that when this fires).
    session_tenant_id?: string;
  };
  // Fires when the logs route returns an empty timeline for a booking
  // whose `created_on` is older than 5 minutes. Real bookings always have
  // at least one `created` action log within seconds of creation, so an
  // empty timeline for an aged booking is an anomaly — usually the
  // cross-tenant bug, or the send_logs Lambda silently filtering rows.
  // Dashboard: filter same_tenant=false to surface the original bug;
  // same_tenant=true indicates a new failure mode worth investigating.
  booking_logs_empty_anomaly: {
    appointment_id: string;
    // Resolved booking tenant_id, when booking-get succeeded.
    booking_tenant_id?: string;
    session_tenant_id?: string;
    // True when session_tenant_id === booking_tenant_id (so the empty
    // result is not explained by the cross-tenant bug).
    same_tenant?: boolean;
    // True when we resolved tenant via booking-get; false means we fell
    // back to session tenant (also fires `booking_logs_tenant_fallback`).
    resolved_via_booking_get: boolean;
    // Bucketed age of the booking at the time of the empty result.
    age_bucket: 'lt_5min' | '5min_30min' | '30min_6hr' | '6hr_24hr' | 'gt_24hr';
  };

  // ----- Allow generic feature flag evaluation event ----------------------
  feature_flag_evaluated: {
    flag_key: string;
    flag_value: boolean | string;
  };

  // ----- Status admin events -----------------------------------------------
  status_admin_created: { status_type: string };
  status_admin_toggled: { status_type: string; new_active_state: boolean };
  status_admin_updated: { status_type: string };
  status_admin_deleted: { status_type: string };
  status_lookup_loaded: {
    source: string;
    type_count: number;
    status_type?: string;
    count?: number;
  };
  status_lookup_fallback_used: { status_type: string; reason?: string };

  // ----- Public /hst home sleep test instructions page --------------------
  // Fires on every load of /hst. The page is unauthenticated and linked from
  // shipping notifications, so all traffic is anonymous — the profile fetch is
  // deliberately skipped there, which is why there is no `is_authenticated`
  // property. PostHog's built-in `$pageview` is also captured by
  // PostHogProvider; this typed event exists so the page can be counted on its
  // own without depending on URL filters, and carries the `?src=` tag from the
  // link the patient followed.
  hst_instructions_page_viewed: {
    link_source?: string;
  };
  // Fires when a patient acts on the page: plays the setup video, expands an
  // FAQ row, or opens the contact modal.
  hst_instructions_engaged: {
    action: 'video_played' | 'faq_expanded' | 'contact_clicked';
    // FAQ row identifier — a fixed slug, never free text.
    faq_id?:
      | 'lost_stolen'
      | 'timely_completion'
      | 'shipping_delays'
      | 'insurance_coverage';
  };
  // Outcome of the /hst "Get in Touch" modal. Mirrors the marketing site's
  // contact_form_success / contact_form_error pair.
  //
  // Only the two dropdown selections are recorded. The name, email, phone and
  // free-text comments a patient types are never sent to PostHog — the
  // comments box in particular is somewhere a patient may describe symptoms,
  // which would be PHI.
  hst_contact_submitted: {
    inquiry_type?: string;
    user_role?: 'patient' | 'provider';
  };
  hst_contact_failed: {
    // A fixed bucket, never the raw error text, which can contain input.
    reason: 'validation' | 'rate_limited' | 'network' | 'server';
  };
}

export type AppEventName = keyof AppEventMap;
export type AppEventProps<E extends AppEventName> = AppEventMap[E];

// ---------------------------------------------------------------------------
// PHI denylist & sanitization
// ---------------------------------------------------------------------------

/**
 * Property keys that must NEVER appear in an event. These are stripped at
 * the call site by `sanitizeProperties` before the event leaves the device.
 *
 * Keep this list aggressive. False positives are easier to fix than a HIPAA
 * incident.
 */
export const PHI_DENYLIST: ReadonlySet<string> = new Set([
  'address',
  'address1',
  'address2',
  'city',
  'date_of_birth',
  'diagnosis',
  'diagnosis_code',
  'diagnoses',
  'dob',
  'email', // allowed only inside posthog.identify, never in event props
  'family_name',
  'fax',
  'first_name',
  'full_name',
  'given_name',
  'icd10',
  'last_name',
  'medical_record_number',
  'message',
  'message_body',
  'middle_name',
  'mrn',
  'name',
  'note',
  'note_body',
  'note_text',
  'patient_address',
  'patient_dob',
  'patient_email',
  'patient_first_name',
  'patient_last_name',
  'patient_name',
  'patient_phone',
  'patient_ssn',
  'phone',
  'phone_number',
  'postal_code',
  'reply_body',
  'search_term',
  'search_query',
  'ssn',
  'state',
  'street',
  'subject_line',
  'text',
  'zip',
  'zip_code',
]);

/**
 * Strip PHI keys from an event property bag before transmission. In dev,
 * stripped keys are surfaced as a `console.warn` so the offending call site
 * gets fixed.
 */
export function sanitizeProperties<T extends Record<string, unknown>>(
  eventName: string,
  properties: T | undefined,
): Record<string, unknown> | undefined {
  if (!properties) return properties;
  const safe: Record<string, unknown> = {};
  const stripped: string[] = [];
  for (const [key, value] of Object.entries(properties)) {
    if (PHI_DENYLIST.has(key.toLowerCase())) {
      stripped.push(key);
      continue;
    }
    safe[key] = value;
  }
  if (stripped.length > 0 && process.env.NODE_ENV !== 'production') {
    console.warn(
      `[PostHog] Stripped potential PHI keys from "${eventName}": ${stripped.join(', ')}. ` +
        `Update the call site or extend AppEventMap.`,
    );
  }
  return safe;
}

// ---------------------------------------------------------------------------
// Utility bucketers — keep raw counts/lengths/durations out of the data lake.
// ---------------------------------------------------------------------------

export function bucketCount(n: number): CountBucket {
  if (n <= 0) return '0';
  if (n === 1) return '1';
  if (n <= 5) return '2_5';
  if (n <= 25) return '6_25';
  if (n <= 100) return '26_100';
  if (n <= 1000) return '101_1000';
  return 'gt_1000';
}

export function bucketDurationMs(ms: number): DurationBucket {
  if (ms < 100) return 'lt_100ms';
  if (ms < 500) return 'lt_500ms';
  if (ms < 1000) return 'lt_1s';
  if (ms < 5000) return 'lt_5s';
  if (ms < 30000) return 'lt_30s';
  return 'gt_30s';
}

/**
 * Bucketer for booking age (created_on → now) when emitting
 * `booking_logs_empty_anomaly`. Kept separate from `bucketDurationMs`
 * because the meaningful thresholds for "is this booking old enough that
 * an empty timeline is suspicious?" are minutes-to-days, not
 * milliseconds-to-seconds.
 */
export function bucketBookingAgeMs(
  ms: number,
): 'lt_5min' | '5min_30min' | '30min_6hr' | '6hr_24hr' | 'gt_24hr' {
  if (ms < 5 * 60_000) return 'lt_5min';
  if (ms < 30 * 60_000) return '5min_30min';
  if (ms < 6 * 60 * 60_000) return '30min_6hr';
  if (ms < 24 * 60 * 60_000) return '6hr_24hr';
  return 'gt_24hr';
}
